import React from 'react';
import { css } from '@emotion/css';
import { Button, Indicator } from 'is24-corecss';

import S24LockClose from '@is24/cosma-ui-icons/svg/s24_lock_close_24.svg';
import { IconS24AlertTriangle24 } from '@is24/cosma-ui-icons';
import { GRAY200, GRAY500, GRAY700, RED500 } from '../../../../../domain/colors';
import { ICON_TYPE, BenefitCardStatus } from '../../../../../utils/getBenefitCardStatus.util';
import { useIntl } from '@/i18n';

const styles = {
  palmSeparator: {
    '@media (min-width: 565px)': {
      display: 'none',
    },
  },
  listitemButton: {
    display: 'flex',
    fontWeight: '400',
    fontSize: '14px',
    lineHeight: '18px',
    minHeight: '54px',
    whiteSpace: 'normal',
    border: 'none !important',
    borderRadius: 0,
    padding: 0,
    backgroundColor: `#FFF !important`,
    '&:hover': {
      backgroundColor: `${GRAY200} !important`,
    },
    '@media (min-width: 565px)': {
      minHeight: '72px',
      fontSize: '16px !important',
      lineHeight: `22px !important`,
      border: `1px solid var(--color-border-weak) !important`,
      borderRadius: '4px',
      height: 72,
      padding: '16px',
    },
    error: {
      '&:hover': {
        backgroundColor: '#FFF !important',
      },
    },
  },
  chevron: {
    marginLeft: '8px',
    marginRight: -2,
  },
  dot: css`
    background-color: var(--color-background-info) !important;
  `,
  flexRight: css`
    margin-left: auto;
    display: flex;
    align-items: center;
  `,
  listItem: css`
    @media only screen and (max-width: 565px) {
      &:not(:last-child) {
        border-bottom: 1px solid ${GRAY500};
      }
    }
  `,
  cardStatus: {
    fontSize: '12px',
    fontWeight: 'normal',
    letterSpacing: 0,
    lineHeight: '16px',
    color: '#757575',
  },
} as const;

export interface BenefitProps {
  title: string;
  defaultTitle: string;
  unviewed: boolean;
  isEntitledForService?: boolean;
  benefitCardStatus?: BenefitCardStatus;
  onClick: () => void;
}

const BenefitCardRightSideContent = ({
  isEntitledForService,
  unviewed,
  iconType,
}: {
  isEntitledForService: boolean;
  unviewed: boolean;
  iconType?: ICON_TYPE | null;
}) => {
  if (iconType === ICON_TYPE.ERROR) {
    return (
      <IconS24AlertTriangle24
        data-testid={`benefit-card-icon-${iconType}`}
        inline={false}
        title='notitle'
        color={RED500}
      />
    );
  }
  if (isEntitledForService) {
    return (
      <>
        {unviewed && <Indicator appearance='dot' className={css(styles.dot)} data-testid='indicator-dot' />}
        <span
          data-testid={`benefit-card-icon-${iconType}`}
          className={`${
            !!iconType && iconType === ICON_TYPE.INFO
              ? 'is24-icon-info margin-left-lg'
              : 'is24-icon-chevron-right button-icon-margin-right'
          } align-right ${css(styles.chevron)}`}
        />
      </>
    );
  }

  return (
    <img src={S24LockClose} width={16} data-testid={'lock-icon'} alt={'Schlosssymbol auf transparentem Untergrund'} />
  );
};

export const BenefitCard = ({
  title,
  defaultTitle,
  unviewed,
  isEntitledForService = true,
  benefitCardStatus,
  onClick,
}: BenefitProps) => {
  const { t } = useIntl();

  return (
    <div role='listitem' aria-labelledby={title} className={`${css(styles.listItem)}`}>
      <Button
        className={`grid grid-flex grid-align-center ${css(
          styles.listitemButton,
          benefitCardStatus?.iconType == ICON_TYPE.ERROR ? styles.listitemButton.error : {},
        )} `}
        onClick={onClick}
        disabled={benefitCardStatus?.iconType == ICON_TYPE.ERROR}
      >
        <div
          id={title}
          className='grid-item ten-twelfth'
          style={benefitCardStatus?.iconType == ICON_TYPE.ERROR ? { color: GRAY700 } : {}}
        >
          {t({ id: title, defaultMessage: defaultTitle })}
          {!!benefitCardStatus?.statusTextTranslationKey && (
            <p className={css(styles.cardStatus)} data-test-ctx={'status-text'}>
              {t({ id: benefitCardStatus?.statusTextTranslationKey })}
            </p>
          )}
        </div>
        <div className={css(styles.flexRight)}>
          <BenefitCardRightSideContent
            isEntitledForService={isEntitledForService}
            unviewed={unviewed}
            iconType={benefitCardStatus?.iconType}
          />
        </div>
      </Button>
    </div>
  );
};
